/**
 * Single source of the mappable Products-entity field list used by the Signify
 * mapping UIs (`useSignifyProductModal`, `useSignifyConfigurationSidebar`).
 * Derives `{ fieldId, label, fieldDataTypeId }` from the entity's default UI
 * layout, dropping fields with no `FieldTypeContext` entry and the ids the
 * integration owns: `manufacturersProductId`, `manufacturersSourceId`, and
 * `SIGNIFY_RECORD_NAME_DESTINATION`, which a Products field short-labelled
 * "Record Name" would generate. Dropping it here rather than where a
 * destination list is assembled covers the session-override lanes too, which
 * offer these fields directly. The cost is that a real Products field with one
 * of those ids is unreachable from Signify.
 *
 * `enabled` gates both the Firestore subscription and the derivation, so a
 * closed sidebar holds no listener. The module-level store is registry-backed
 * and refcounted, so concurrent consumers share one listener.
 */
import { useContext, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { Entity, FieldType } from '#types/System.types';
import { SIGNIFY_RECORD_NAME_DESTINATION } from '#types';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { createDocumentStore } from '@/store';
import { extractEntityMappingData } from '@/components/settings/ArTableEditorTab/extractEntityMappingData';

const productsEntityStore = createDocumentStore<Entity>('entities/Products');

export type SignifyProductField = {
    fieldId: string;
    label: string;
    fieldDataTypeId: FieldType['fieldDataTypeId'];
};

export const useSignifyProductFields = (enabled: boolean) => {
    const { fieldTypes } = useContext(FieldTypeContext);

    useEffect(() => {
        if (!enabled) return;
        productsEntityStore.connect();
        return () => productsEntityStore.disconnect();
    }, [enabled]);

    const productsEntitySnap = useSnapshot(productsEntityStore.state);
    const productsEntity = enabled ? (productsEntitySnap.data as Entity | null) : null;

    const availableProductFields = useMemo<SignifyProductField[]>(() => {
        if (!productsEntity) return [];

        return extractEntityMappingData(productsEntity)
            .fields
            .map((field) => {
                const fieldType = fieldTypes[field.fieldId];
                if (!fieldType) {
                    console.warn(`Field type not found for fieldId: ${field.fieldId}`);
                    return null;
                }

                if (field.fieldId === 'manufacturersProductId' ||
                    field.fieldId === 'manufacturersSourceId' ||
                    field.fieldId === SIGNIFY_RECORD_NAME_DESTINATION) {
                    return null;
                }

                const label = field.description?.shortLabel || fieldType.description?.shortLabel || field.fieldId;

                return {
                    fieldId: field.fieldId,
                    label,
                    fieldDataTypeId: fieldType.fieldDataTypeId
                };
            })
            .filter((field) => field !== null)
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [productsEntity, fieldTypes]);

    return { availableProductFields, productsEntity };
};
